import React, { createContext, useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useAlert } from '../../alert/containers/AlertProvider'
import {
  getContributorListApi,
  getProfessionsApi,
  getAgeRangesApi,
} from '../api'
import { useTranslation } from 'react-i18next'

export const ContributorsContext = createContext()

export const initialFilter = {
  orderBy: 0,
  genderIds: [],
  professionIds: [],
  wilayas: [],
  communes: [],
  ageRanges: [],
  phoneNumber: '',
  // mem
  dairaId: [],
}

const initialPagination = { currentPage: 0, take: 10, skip: 0, total: 0 }


export const ContributorsProvider = ({ children }) => {

  //
  const [professionsPopOver, setprofessionsPopOver] = useState([])

  const { showAlert } = useAlert()
  const [isLoading, setIsLoading] = useState(false)
  const { t, i18n } = useTranslation()

  const [professions, setProfessions] = useState([])
  const [ageRanges, setAgeRanges] = useState([])

  const [contributorList, setContributorList] = useState([])
  const [pagination, setPagination] = useState(initialPagination)

  const [filter, setFilter] = useState(initialFilter)
  // Todo: set skip to 0 after each setFilter
  const handlePagination = ({
    take = pagination.take,
    skip = pagination.skip,
    currentPage = pagination.currentPage,
  }) => {
    setPagination({ ...pagination, take, skip, currentPage })
  }

  const handleFilter = (newFilter) => {
    handlePagination({ currentPage: 0, skip: 0 })
    setFilter(newFilter)
  }

  const getProfessions = async () => {
    try {
      setIsLoading(true)
      const { data } = await getProfessionsApi()
      setProfessions(data)
    } catch {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } finally {
      setIsLoading(false)
    }
  }

  const getAgeRanges = async () => {
    try {
      setIsLoading(true)
      const { data } = await getAgeRangesApi()
      setAgeRanges(data)
    } catch {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } finally {
      setIsLoading(false)
    }
  }

  const getContributorsList = async () => {
    try {
      const {
        professionIds,
        genderIds,
        wilayas,
        communes,
        ageRanges: filterAgeRange,
        phoneNumber,
        orderBy,
        // mem
        dairaId,
      } = filter
      const { take, skip } = pagination

      setIsLoading(true)

      const requestFilter = {
        professions: professionIds,
        genders: genderIds,
        communeIds: communes.map(({ id }) => id),
        // mem
        dairaIds: dairaId.map(({ id }) => id),
        //
        wilayaIds: communes.length === 0 ? wilayas.map(({ id }) => id) : [],
        orderBy,
        ageRanges: filterAgeRange,
        ...(phoneNumber.length > 3 && { phoneNumber }),
        take,
        skip,
      }

      const { data, headers } = await getContributorListApi({
        filter: requestFilter,
      })

      // here edit
      const data2 = data.map( element => { return {...element, phoneNumber: element.phoneNumber.replace('\+213','0')} } )
      setContributorList(data2)

      setPagination({ ...pagination, total: headers['x-total-count'] })
    } catch (e) {
      showAlert({ message: t('showAlert.une_erreur_est_survenue') })
    } finally {
      setIsLoading(false)
    }
  }

  const init = () => {
    setFilter({ ...initialFilter })
    setPagination({ ...initialPagination })
  }

  //
  const mapProfessionsPopOver = () => {
    let finalResult = []

    if (i18n.language === "fr")
      finalResult = professions.map(({id, label}) => ({id:id, label:label}) ) // mmm
    else
      finalResult = professions.map(({id, label_ar}) => ({id:id, label:label_ar}) )
    
    setprofessionsPopOver(finalResult)
  }

  const providerValue = {
    init,
    contributorList,
    professions,
    // here edit:
    /* professionsPopOver: [
      { id: -1, label: t('professions.nonPrecised')},
      { id: 1, label: t('professions.sansEmploi')},
      { id: 2, label: t('professions.etudiant')},
      { id: 3, label: t('professions.employed')},
      { id: 4, label: t('professions.commercant_liberalProfession')},
      { id: 5, label: t('professions.chefEntreprise')},
      { id: 6, label: t('professions.other')},
    ], */
    ageRanges,
    genders: [
      { id: 1, label: t('genders.homme')},
      { id: 2, label: t('genders.femme')},
      { id: -1, label: t('genders.nonPrecised')}
    ],
    filter,
    pagination,
    isLoading,
    handleFilter,
    handlePagination,
    getContributorsList,
    professionsPopOver,
  }

  useEffect(() => {
    if (professions.length > 0) {

      //
      mapProfessionsPopOver()

      getContributorsList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination.take, pagination.skip, professions, ageRanges])

  useEffect(() => {
    getProfessions()
    getAgeRanges()

    //
    mapProfessionsPopOver()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ContributorsContext.Provider value={providerValue}>
      {children}
    </ContributorsContext.Provider>
  )
}

ContributorsProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useContributors = () => {
  const context = useContext(ContributorsContext)
  if (context === undefined) {
    throw new Error(
      'useContributors must be used within a ContributorsProvider',
    )
  }
  return context
}
